import React, { useState, useEffect } from 'react';
import { X, Plus, Users, Lock, Globe, Search, Edit, Trash2, Crown, Shield, User, Film, ChevronRight, Image, Mail, UserPlus, Gamepad2, Check, Clock, LogOut } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import axios from 'axios';
import { API_URL } from '../config/api';
import { toast } from 'sonner';
import LastPersonStanding from './LastPersonStanding';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w200';
const TMDB_BEARER_TOKEN = process.env.REACT_APP_TMDB_BEARER_TOKEN;

// Invite Member Modal
const InviteMemberModal = ({ isOpen, onClose, club, user, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setSearchQuery('');
      setSearchResults([]);
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const searchUsers = async () => {
    if (searchQuery.length < 2) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API_URL}/api/users/search?query=${encodeURIComponent(searchQuery)}&limit=5`);
      setSearchResults(response.data);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleInvite = async (userEmail) => {
    setSending(true);
    setError('');
    setSuccess('');

    try {
      const res = await axios.post(`${API_URL}/api/clubs/${club.id}/invite?user_id=${user.id}`, {
        user_email: userEmail
      });
      if (res.data.pending_signup) {
        setSuccess(`${userEmail} isn't on Casitaplex yet. A signup invitation has been sent — they'll be auto-added to the club when they register!`);
      } else {
        setSuccess(`Invitation sent to ${userEmail}`);
      }
      setEmail('');
      setSearchQuery('');
      setSearchResults([]);
      onSuccess && onSuccess();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const handleAddDirectly = async (userEmail) => {
    setSending(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_URL}/api/clubs/${club.id}/add-member?user_email=${encodeURIComponent(userEmail)}&user_id=${user.id}`);
      setSuccess(`Added ${userEmail} to the club`);
      setEmail('');
      setSearchQuery('');
      setSearchResults([]);
      onSuccess && onSuccess();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md p-0 [&>button]:hidden">
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-bold">Invite to {club?.name}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Search Users */}
          <div>
            <Label className="text-gray-300">Search Members</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                placeholder="Search by name or email..."
                className="bg-zinc-800 border-zinc-700 text-white"
              />
              <Button onClick={searchUsers} disabled={searching} className="bg-red-600 hover:bg-red-700">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2">
              <Label className="text-gray-400 text-sm">Found Users</Label>
              {searchResults.map((foundUser) => (
                <div key={foundUser.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-semibold overflow-hidden">
                      {foundUser.profile_picture ? (
                        <img src={foundUser.profile_picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (foundUser.first_name?.[0] || 'U')
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {foundUser.display_name || `${foundUser.first_name} ${foundUser.last_name}`}
                      </p>
                      <p className="text-xs text-gray-400">{foundUser.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      onClick={() => handleInvite(foundUser.email)}
                      disabled={sending}
                      size="sm"
                      variant="outline"
                      className="border-zinc-600 text-xs"
                    >
                      <Mail className="h-3 w-3 mr-1" />
                      Invite
                    </Button>
                    <Button
                      onClick={() => handleAddDirectly(foundUser.email)}
                      disabled={sending}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-xs"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Direct Email Invite */}
          <div className="border-t border-zinc-700 pt-4">
            <Label className="text-gray-300">Or invite by email</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@email.com"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
              <Button
                onClick={() => handleInvite(email)}
                disabled={sending || !email}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {sending ? 'Sending...' : 'Invite'}
              </Button>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
          {success && <p className="text-green-500 text-sm">{success}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Create/Edit Club Modal
const ClubModal = ({ isOpen, onClose, club, user, onSave }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [avatar, setAvatar] = useState('');
  const [banner, setBanner] = useState('');
  const [privacy, setPrivacy] = useState('public');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Avatar search
  const [avatarSearch, setAvatarSearch] = useState('');
  const [avatarResults, setAvatarResults] = useState([]);
  const [searchingAvatars, setSearchingAvatars] = useState(false);

  useEffect(() => {
    if (club) {
      setName(club.name || '');
      setDescription(club.description || '');
      setAvatar(club.avatar || '');
      setBanner(club.banner || '');
      setPrivacy(club.privacy || 'public');
    } else {
      setName('');
      setDescription('');
      setAvatar('');
      setBanner('');
      setPrivacy('public');
    }
    setError('');
  }, [club, isOpen]);

  const searchAvatars = async () => {
    if (!avatarSearch.trim()) return;
    
    setSearchingAvatars(true);
    try {
      const response = await axios.get(
        `https://api.themoviedb.org/3/search/multi`,
        {
          params: { query: avatarSearch },
          headers: { Authorization: `Bearer ${TMDB_BEARER_TOKEN}` }
        }
      );
      
      const results = response.data.results
        .filter(item => item.poster_path)
        .slice(0, 8)
        .map(item => ({
          id: item.id,
          title: item.title || item.name,
          image: `${TMDB_IMAGE_BASE}${item.poster_path}`
        }));
      
      setAvatarResults(results);
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setSearchingAvatars(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Club name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (club) {
        // Update existing club
        await axios.put(`${API_URL}/api/clubs/${club.id}?user_id=${user.id}`, {
          name,
          description,
          avatar,
          banner,
          privacy
        });
      } else {
        // Create new club
        await axios.post(`${API_URL}/api/clubs?user_id=${user.id}`, {
          name,
          description,
          avatar,
          banner,
          privacy
        });
      }
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save club');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg max-h-[90vh] overflow-hidden p-0 [&>button]:hidden">
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <h2 className="text-xl font-bold">{club ? 'Edit Club' : 'Create New Club'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
          {/* Club Avatar Preview */}
          <div className="flex justify-center">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-zinc-700 flex items-center justify-center">
              {avatar ? (
                <img src={avatar} alt="Club Avatar" className="w-full h-full object-cover" />
              ) : (
                <Users className="h-10 w-10 text-gray-500" />
              )}
            </div>
          </div>

          <div>
            <Label className="text-gray-300">Club Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white mt-1"
              placeholder="Enter club name"
              maxLength={50}
            />
          </div>

          <div>
            <Label className="text-gray-300">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white mt-1 min-h-[80px]"
              placeholder="What is this club about?"
              maxLength={500}
            />
          </div>

          {/* Avatar Search */}
          <div>
            <Label className="text-gray-300">Club Avatar (Movie/Show)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={avatarSearch}
                onChange={(e) => setAvatarSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchAvatars()}
                className="bg-zinc-800 border-zinc-700 text-white"
                placeholder="Search movies/shows..."
              />
              <Button onClick={searchAvatars} disabled={searchingAvatars} className="bg-red-600 hover:bg-red-700">
                <Search className="h-4 w-4" />
              </Button>
            </div>
            {avatarResults.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {avatarResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => { setAvatar(result.image); setAvatarResults([]); }}
                    className={`rounded overflow-hidden border-2 transition-all ${
                      avatar === result.image ? 'border-red-600' : 'border-transparent hover:border-zinc-500'
                    }`}
                  >
                    <img src={result.image} alt={result.title} className="w-full aspect-[2/3] object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Banner URL */}
          <div>
            <Label className="text-gray-300">Banner Image URL</Label>
            <Input
              value={banner}
              onChange={(e) => setBanner(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white mt-1"
              placeholder="https://example.com/banner.jpg"
            />
          </div>

          {/* Privacy Setting */}
          <div className="bg-zinc-800 rounded-lg p-4">
            <Label className="text-gray-300 block mb-3">Privacy</Label>
            <div className="space-y-3">
              <button
                onClick={() => setPrivacy('public')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  privacy === 'public' ? 'border-red-600 bg-red-600/20' : 'border-zinc-700 hover:border-zinc-500'
                }`}
              >
                <Globe className="h-5 w-5" />
                <div className="text-left">
                  <p className="font-medium">Public</p>
                  <p className="text-sm text-gray-400">Anyone can view and join</p>
                </div>
              </button>
              <button
                onClick={() => setPrivacy('private')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  privacy === 'private' ? 'border-red-600 bg-red-600/20' : 'border-zinc-700 hover:border-zinc-500'
                }`}
              >
                <Lock className="h-5 w-5" />
                <div className="text-left">
                  <p className="font-medium">Private</p>
                  <p className="text-sm text-gray-400">Invite or request to join</p>
                </div>
              </button>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>

        <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-zinc-700">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700">
            {saving ? 'Saving...' : (club ? 'Save Changes' : 'Create Club')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Club Card Component
const ClubCard = ({ club, user, onClick }) => {
  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="h-3 w-3 text-yellow-500" />;
      case 'admin': return <Shield className="h-3 w-3 text-blue-500" />;
      default: return null;
    }
  };

  return (
    <div
      onClick={onClick}
      className="bg-zinc-800 rounded-lg overflow-hidden cursor-pointer hover:bg-zinc-700 transition-colors"
    >
      {/* Banner */}
      <div className="h-20 bg-gradient-to-r from-red-900 to-red-600 relative">
        {club.banner && (
          <img src={club.banner} alt="" className="w-full h-full object-cover absolute inset-0" />
        )}
      </div>
      
      {/* Content */}
      <div className="p-4 relative">
        {/* Avatar */}
        <div className="absolute -top-8 left-4 w-16 h-16 rounded-full bg-zinc-700 border-4 border-zinc-800 overflow-hidden">
          {club.avatar ? (
            <img src={club.avatar} alt={club.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Users className="h-8 w-8 text-gray-500" />
            </div>
          )}
        </div>

        <div className="ml-16 pt-2">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-white truncate">{club.name}</h3>
            {club.privacy === 'private' && <Lock className="h-3 w-3 text-gray-400" />}
            {club.user_role && getRoleIcon(club.user_role)}
          </div>
          <p className="text-sm text-gray-400">{club.member_count} members</p>
        </div>

        {club.description && (
          <p className="text-sm text-gray-400 mt-2 line-clamp-2">{club.description}</p>
        )}
      </div>
    </div>
  );
};

// Main Clubs Component
const Clubs = ({ user, isOpen, onClose }) => {
  const [clubs, setClubs] = useState([]);
  const [userClubs, setUserClubs] = useState([]);
  const [allMemberships, setAllMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [activeTab, setActiveTab] = useState('my-clubs');
  const [searchQuery, setSearchQuery] = useState('');
  const [joiningClubId, setJoiningClubId] = useState(null);

  useEffect(() => {
    if (isOpen && user) {
      fetchClubs();
    }
  }, [isOpen, user]);

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const [userClubsRes, allClubsRes, membershipsRes] = await Promise.all([
        axios.get(`${API_URL}/api/clubs/user/${user.id}`),
        axios.get(`${API_URL}/api/clubs?user_id=${user.id}`),
        axios.get(`${API_URL}/api/clubs/user/${user.id}/memberships`),
      ]);
      setUserClubs(userClubsRes.data);
      setClubs(allClubsRes.data);
      setAllMemberships(membershipsRes.data);
    } catch (err) {
      console.error('Error fetching clubs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getMembershipStatus = (clubId) => {
    const m = allMemberships.find(ms => ms.club_id === clubId);
    return m ? m.status : null;
  };

  const handleJoinClub = async (clubId, privacy) => {
    setJoiningClubId(clubId);
    try {
      const res = await axios.post(`${API_URL}/api/clubs/${clubId}/join?user_id=${user.id}`);
      if (res.data.membership_status === 'active') {
        toast.success('Joined club successfully!');
      } else {
        toast.success('Join request sent! Waiting for admin approval.');
      }
      fetchClubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join club');
    } finally {
      setJoiningClubId(null);
    }
  };

  const handleCancelRequest = async (clubId) => {
    setJoiningClubId(clubId);
    try {
      await axios.post(`${API_URL}/api/clubs/${clubId}/cancel-request?user_id=${user.id}`);
      toast.success('Join request cancelled');
      fetchClubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel request');
    } finally {
      setJoiningClubId(null);
    }
  };

  const filteredClubs = clubs.filter(club => 
    club.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    club.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const discoverClubs = filteredClubs.filter(club => 
    !userClubs.find(uc => uc.id === club.id)
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-4xl max-h-[90vh] overflow-hidden p-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-red-500" />
            <h2 className="text-xl font-bold">Clubs</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowCreateModal(true)} className="bg-red-600 hover:bg-red-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Club
            </Button>
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-700">
          <button
            onClick={() => setActiveTab('my-clubs')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'my-clubs' ? 'border-b-2 border-red-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            My Clubs ({userClubs.length})
          </button>
          <button
            onClick={() => setActiveTab('discover')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'discover' ? 'border-b-2 border-red-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            Discover
          </button>
        </div>

        {/* Search */}
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white pl-10"
              placeholder="Search clubs..."
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-red-600 border-t-transparent rounded-full"></div>
            </div>
          ) : activeTab === 'my-clubs' ? (
            userClubs.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">You have not joined any clubs yet</p>
                <Button onClick={() => setActiveTab('discover')} className="mt-4 bg-red-600 hover:bg-red-700">
                  Discover Clubs
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {userClubs.map(club => (
                  <ClubCard
                    key={club.id}
                    club={club}
                    user={user}
                    onClick={() => setSelectedClub(club)}
                  />
                ))}
              </div>
            )
          ) : (
            discoverClubs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400">No clubs found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {discoverClubs.map(club => {
                  const status = getMembershipStatus(club.id);
                  const isPending = status === 'pending';
                  const isInvited = status === 'invited';
                  const isJoining = joiningClubId === club.id;
                  return (
                    <div key={club.id} className="bg-zinc-800 rounded-lg overflow-hidden">
                      <ClubCard club={club} user={user} onClick={() => setSelectedClub(club)} />
                      <div className="p-3 border-t border-zinc-700">
                        {isPending ? (
                          <div className="flex gap-2">
                            <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-md bg-amber-900/20 border border-amber-700/40 text-amber-400 text-sm">
                              <Clock className="h-4 w-4 flex-shrink-0" />
                              <span>Request Pending</span>
                            </div>
                            <Button
                              onClick={() => handleCancelRequest(club.id)}
                              disabled={isJoining}
                              variant="outline"
                              className="border-zinc-600 text-gray-400 hover:text-red-400 hover:border-red-600 text-xs"
                              data-testid={`cancel-request-${club.id}`}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : isInvited ? (
                          <Button
                            onClick={() => handleJoinClub(club.id, club.privacy)}
                            disabled={isJoining}
                            className="w-full bg-green-600 hover:bg-green-700"
                            data-testid={`accept-invite-${club.id}`}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Accept Invitation
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleJoinClub(club.id, club.privacy)}
                            disabled={isJoining}
                            className="w-full bg-red-600 hover:bg-red-700"
                            data-testid={`join-club-${club.id}`}
                          >
                            {isJoining ? (
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                            ) : club.privacy === 'private' ? (
                              <Lock className="h-4 w-4 mr-2" />
                            ) : (
                              <Plus className="h-4 w-4 mr-2" />
                            )}
                            {club.privacy === 'private' ? 'Request to Join' : 'Join Club'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {/* Create Club Modal */}
        <ClubModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          club={null}
          user={user}
          onSave={fetchClubs}
        />

        {/* Club Detail Modal */}
        {selectedClub && (
          <ClubDetail
            club={selectedClub}
            user={user}
            onClose={() => setSelectedClub(null)}
            onUpdate={fetchClubs}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// Club Detail Component
const ClubDetail = ({ club, user, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('feed');
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [postVisibility, setPostVisibility] = useState('club_only');
  const [posting, setPosting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [clubData, setClubData] = useState(club);

  // Watchlist search
  const [watchlistSearch, setWatchlistSearch] = useState('');
  const [watchlistResults, setWatchlistResults] = useState([]);
  const [searchingWatchlist, setSearchingWatchlist] = useState(false);

  // Last Person Standing game modal
  const [gameMovie, setGameMovie] = useState(null);

  useEffect(() => {
    fetchClubData();
  }, [club.id]);

  const fetchClubData = async () => {
    setLoading(true);
    try {
      // Fetch club details
      const clubRes = await axios.get(`${API_URL}/api/clubs/${club.id}?user_id=${user.id}`);
      setClubData(clubRes.data);

      const isMember = clubRes.data.user_role && clubRes.data.user_status === 'active';
      const isPublic = clubRes.data.privacy === 'public';

      // Only fetch detailed data if member or public club
      if (isMember || isPublic) {
        const [postsRes, membersRes, watchlistRes] = await Promise.all([
          axios.get(`${API_URL}/api/clubs/${club.id}/posts?user_id=${user.id}`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/clubs/${club.id}/members?user_id=${user.id}`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/clubs/${club.id}/watchlist?user_id=${user.id}`).catch(() => ({ data: [] })),
        ]);
        setPosts(postsRes.data);
        setMembers(membersRes.data);
        setWatchlist(watchlistRes.data);
      }
    } catch (err) {
      // For private clubs, we may get 403 — just show limited info
      console.error('Error fetching club data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim()) return;

    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/clubs/${club.id}/posts?user_id=${user.id}`, {
        content: newPost,
        visibility: postVisibility
      });
      setNewPost('');
      fetchClubData();
    } catch (err) {
      console.error('Error creating post:', err);
    } finally {
      setPosting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/api/clubs/${club.id}/posts/${postId}?user_id=${user.id}`);
      fetchClubData();
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const searchWatchlistItems = async () => {
    if (!watchlistSearch.trim()) return;

    setSearchingWatchlist(true);
    try {
      const response = await axios.get(
        `https://api.themoviedb.org/3/search/multi`,
        {
          params: { query: watchlistSearch },
          headers: { Authorization: `Bearer ${TMDB_BEARER_TOKEN}` }
        }
      );

      const results = response.data.results
        .filter(item => (item.media_type === 'movie' || item.media_type === 'tv') && item.poster_path)
        .slice(0, 6)
        .map(item => ({
          tmdb_id: item.id,
          media_type: item.media_type,
          title: item.title || item.name,
          poster_path: item.poster_path,
          genre_ids: item.genre_ids || [],
        }));

      setWatchlistResults(results);
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setSearchingWatchlist(false);
    }
  };

  const addToWatchlist = async (item) => {
    try {
      await axios.post(`${API_URL}/api/clubs/${club.id}/watchlist?user_id=${user.id}`, item);
      setWatchlistResults([]);
      setWatchlistSearch('');
      fetchClubData();
    } catch (err) {
      console.error('Error adding to watchlist:', err);
      alert(err.response?.data?.detail || 'Failed to add');
    }
  };

  const removeFromWatchlist = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/api/clubs/${club.id}/watchlist/${itemId}?user_id=${user.id}`);
      fetchClubData();
    } catch (err) {
      console.error('Error removing from watchlist:', err);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/clubs/${club.id}/members/${memberId}/role?role=${newRole}&user_id=${user.id}`);
      fetchClubData();
    } catch (err) {
      console.error('Error updating role:', err);
      alert(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await axios.delete(`${API_URL}/api/clubs/${club.id}/members/${memberId}?user_id=${user.id}`);
      fetchClubData();
    } catch (err) {
      console.error('Error removing member:', err);
      alert(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleLeaveClub = async () => {
    if (!window.confirm('Are you sure you want to leave this club?')) return;
    try {
      await axios.post(`${API_URL}/api/clubs/${club.id}/leave?user_id=${user.id}`);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error leaving club:', err);
      alert(err.response?.data?.detail || 'Failed to leave club');
    }
  };

  const handleDeleteClub = async () => {
    if (!window.confirm('Are you sure you want to delete this club? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/clubs/${club.id}?user_id=${user.id}`);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error deleting club:', err);
      alert(err.response?.data?.detail || 'Failed to delete club');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'admin': return <Shield className="h-4 w-4 text-blue-500" />;
      default: return <User className="h-4 w-4 text-gray-400" />;
    }
  };

  const canManage = clubData.user_role === 'owner' || clubData.user_role === 'admin';
  const isOwner = clubData.user_role === 'owner';
  const isMember = clubData.user_status === 'active';
  const isPendingRequest = clubData.user_status === 'pending';
  const isInvited = clubData.user_status === 'invited';
  const [joiningDetail, setJoiningDetail] = useState(false);

  const handleJoinFromDetail = async () => {
    setJoiningDetail(true);
    try {
      const res = await axios.post(`${API_URL}/api/clubs/${club.id}/join?user_id=${user.id}`);
      if (res.data.membership_status === 'active') {
        toast.success('Joined club successfully!');
      } else {
        toast.success('Join request sent! Waiting for admin approval.');
      }
      fetchClubData();
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join club');
    } finally {
      setJoiningDetail(false);
    }
  };

  const handleCancelFromDetail = async () => {
    setJoiningDetail(true);
    try {
      await axios.post(`${API_URL}/api/clubs/${club.id}/cancel-request?user_id=${user.id}`);
      toast.success('Join request cancelled');
      fetchClubData();
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel request');
    } finally {
      setJoiningDetail(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-3xl max-h-[90vh] overflow-hidden p-0 [&>button]:hidden">
        {/* Header with Banner */}
        <div className="relative">
          <div className="h-32 bg-gradient-to-r from-red-900 to-red-600">
            {clubData.banner && (
              <img src={clubData.banner} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          <button onClick={onClose} className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full">
            <X className="h-5 w-5" />
          </button>
          
          {/* Avatar */}
          <div className="absolute -bottom-10 left-6 w-20 h-20 rounded-full bg-zinc-700 border-4 border-zinc-900 overflow-hidden">
            {clubData.avatar ? (
              <img src={clubData.avatar} alt={clubData.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Users className="h-10 w-10 text-gray-500" />
              </div>
            )}
          </div>
        </div>

        {/* Club Info */}
        <div className="px-6 pt-12 pb-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold">{clubData.name}</h2>
                {clubData.privacy === 'private' && <Lock className="h-4 w-4 text-gray-400" />}
              </div>
              <p className="text-gray-400 text-sm">{clubData.member_count} members</p>
              {clubData.description && (
                <p className="text-gray-300 mt-2">{clubData.description}</p>
              )}
            </div>
            
            {canManage && (
              <div className="flex gap-2">
                <Button onClick={() => setShowInviteModal(true)} className="bg-purple-600 hover:bg-purple-700">
                  <UserPlus className="h-4 w-4 mr-1" /> Invite
                </Button>
                <Button onClick={() => setShowEditModal(true)} variant="outline" className="border-zinc-700">
                  <Edit className="h-4 w-4 mr-1" /> Edit
                </Button>
                {isOwner && (
                  <Button onClick={handleDeleteClub} variant="outline" className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Non-member view */}
        {!isMember && !loading ? (
          <div className="p-6 text-center space-y-4" data-testid="club-nonmember-view">
            {isPendingRequest ? (
              <>
                <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-amber-900/20 border border-amber-700/40 text-amber-400 mx-auto max-w-sm">
                  <Clock className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">Your join request is pending approval</span>
                </div>
                <Button
                  onClick={handleCancelFromDetail}
                  disabled={joiningDetail}
                  variant="outline"
                  className="border-zinc-600 text-gray-400 hover:text-red-400 hover:border-red-600"
                  data-testid="cancel-request-detail"
                >
                  Cancel Request
                </Button>
              </>
            ) : isInvited ? (
              <Button
                onClick={handleJoinFromDetail}
                disabled={joiningDetail}
                className="bg-green-600 hover:bg-green-700"
                data-testid="accept-invite-detail"
              >
                <Check className="h-4 w-4 mr-2" />Accept Invitation & Join
              </Button>
            ) : (
              <>
                {clubData.privacy === 'private' && (
                  <p className="text-gray-400 text-sm">This is a private club. Request to join to see posts, watchlist, and members.</p>
                )}
                <Button
                  onClick={handleJoinFromDetail}
                  disabled={joiningDetail}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="join-club-detail"
                >
                  {joiningDetail ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  ) : clubData.privacy === 'private' ? (
                    <Lock className="h-4 w-4 mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {clubData.privacy === 'private' ? 'Request to Join' : 'Join Club'}
                </Button>
              </>
            )}
          </div>
        ) : (
        <>
        {/* Tabs */}
        <div className="flex border-b border-zinc-700 px-6">
          {['feed', 'watchlist', 'members'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-4 text-sm font-medium capitalize transition-colors ${
                activeTab === tab ? 'border-b-2 border-red-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto max-h-[40vh]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-red-600 border-t-transparent rounded-full"></div>
            </div>
          ) : activeTab === 'feed' ? (
            <div className="space-y-4">
              {/* Create Post */}
              <div className="bg-zinc-800 rounded-lg p-4">
                <Textarea
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  placeholder="Share something with the club..."
                  className="bg-zinc-700 border-none text-white min-h-[60px]"
                />
                <div className="flex items-center justify-between mt-3">
                  <select
                    value={postVisibility}
                    onChange={(e) => setPostVisibility(e.target.value)}
                    className="bg-zinc-700 text-white text-sm rounded px-2 py-1 border-none"
                  >
                    <option value="club_only">Club Only</option>
                    <option value="public">Public</option>
                  </select>
                  <Button onClick={handleCreatePost} disabled={posting || !newPost.trim()} className="bg-red-600 hover:bg-red-700">
                    {posting ? 'Posting...' : 'Post'}
                  </Button>
                </div>
              </div>

              {/* Posts */}
              {posts.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No posts yet</p>
              ) : (
                posts.map(post => (
                  <div key={post.id} className="bg-zinc-800 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-semibold overflow-hidden">
                          {post.user?.profile_picture ? (
                            <img src={post.user.profile_picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            (post.user?.first_name?.[0] || 'U')
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{post.user?.display_name || `${post.user?.first_name} ${post.user?.last_name}`}</p>
                          <p className="text-xs text-gray-400">
                            {new Date(post.created_at).toLocaleDateString()} · {post.visibility === 'public' ? 'Public' : 'Club Only'}
                          </p>
                        </div>
                      </div>
                      {(post.user_id === user.id || canManage) && (
                        <button onClick={() => handleDeletePost(post.id)} className="text-gray-400 hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <p className="mt-3 whitespace-pre-wrap">{post.content}</p>
                  </div>
                ))
              )}
            </div>
          ) : activeTab === 'watchlist' ? (
            <div className="space-y-4">
              {/* Add to Watchlist */}
              <div className="flex gap-2">
                <Input
                  value={watchlistSearch}
                  onChange={(e) => setWatchlistSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchWatchlistItems()}
                  placeholder="Search movies/shows to add..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                <Button onClick={searchWatchlistItems} disabled={searchingWatchlist} className="bg-red-600 hover:bg-red-700">
                  <Search className="h-4 w-4" />
                </Button>
              </div>

              {/* Search Results */}
              {watchlistResults.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {watchlistResults.map(item => (
                    <button
                      key={`${item.media_type}-${item.tmdb_id}`}
                      onClick={() => addToWatchlist(item)}
                      className="flex items-center gap-2 p-2 bg-zinc-800 rounded hover:bg-zinc-700"
                    >
                      <img
                        src={`${TMDB_IMAGE_BASE}${item.poster_path}`}
                        alt={item.title}
                        className="w-12 h-18 object-cover rounded"
                      />
                      <div className="text-left flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        <p className="text-xs text-gray-400 capitalize">{item.media_type}</p>
                      </div>
                      <Plus className="h-4 w-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              )}

              {/* Watchlist Items */}
              {watchlist.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No items in watchlist</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {watchlist.map(item => {
                    const isTvShow = item.media_type === 'tv';
                    return (
                      <div key={item.id} className="bg-zinc-800 rounded-lg overflow-hidden group relative">
                        <img
                          src={`${TMDB_IMAGE_BASE}${item.poster_path}`}
                          alt={item.title}
                          className="w-full aspect-[2/3] object-cover"
                        />
                        {/* Play button — top right, for TV shows (elimination games) */}
                        {isTvShow && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setGameMovie(item); }}
                            className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-600/90 hover:bg-indigo-500 text-white text-[10px] font-semibold backdrop-blur-sm transition-colors z-10 shadow-lg"
                            data-testid={`lps-play-btn-${item.tmdb_id}`}
                          >
                            <Gamepad2 className="h-3 w-3" />
                            Play
                          </button>
                        )}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                          <p className="text-sm font-medium">{item.title}</p>
                          <p className="text-xs text-gray-400">Added by {item.added_by_user?.display_name || item.added_by_user?.first_name}</p>
                          {(item.added_by === user.id || canManage) && (
                            <button
                              onClick={() => removeFromWatchlist(item.id)}
                              className="absolute top-2 left-2 p-1 bg-red-600 rounded"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Members Tab */
            <div className="space-y-2">
              {/* Active Members Section */}
              {members.filter(m => m.status === 'active').length > 0 && (
                <>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Active Members ({members.filter(m => m.status === 'active').length})</h4>
                  {members.filter(m => m.status === 'active').map(membership => (
                    <div key={membership.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-semibold overflow-hidden">
                          {membership.user?.profile_picture ? (
                            <img src={membership.user.profile_picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            (membership.user?.first_name?.[0] || 'U')
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              {membership.user?.display_name || `${membership.user?.first_name} ${membership.user?.last_name}`}
                            </p>
                            {getRoleIcon(membership.role)}
                          </div>
                          <p className="text-xs text-gray-400 capitalize">{membership.role}</p>
                        </div>
                      </div>

                      {canManage && membership.user_id !== user.id && membership.role !== 'owner' && (
                        <div className="flex items-center gap-2">
                          {isOwner && (
                            <select
                              value={membership.role}
                              onChange={(e) => handleUpdateRole(membership.user_id, e.target.value)}
                              className="bg-zinc-700 text-white text-sm rounded px-2 py-1 border-none"
                            >
                              <option value="member">Member</option>
                              <option value="admin">Admin</option>
                            </select>
                          )}
                          <button
                            onClick={() => handleRemoveMember(membership.user_id)}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Invited Members Section */}
              {members.filter(m => m.status === 'invited').length > 0 && (
                <>
                  <h4 className="text-sm font-medium text-yellow-500 mt-4 mb-2 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Pending Invitations ({members.filter(m => m.status === 'invited').length})
                  </h4>
                  {members.filter(m => m.status === 'invited').map(membership => (
                    <div key={membership.id} className="flex items-center justify-between p-3 bg-zinc-800/60 rounded-lg border border-yellow-600/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-zinc-600 flex items-center justify-center text-white font-semibold overflow-hidden opacity-70">
                          {membership.user?.profile_picture ? (
                            <img src={membership.user.profile_picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            (membership.user?.first_name?.[0] || 'U')
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-300">
                              {membership.user?.display_name || `${membership.user?.first_name} ${membership.user?.last_name}`}
                            </p>
                            <span className="px-2 py-0.5 bg-yellow-600/20 text-yellow-500 text-xs rounded-full">
                              Invited
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Invited by {membership.inviter?.display_name || membership.inviter?.first_name || 'Unknown'}
                            {membership.invited_at && ` • ${new Date(membership.invited_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>

                      {canManage && (
                        <button
                          onClick={() => handleRemoveMember(membership.user_id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-zinc-700 rounded"
                          title="Cancel invitation"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Pending Requests Section (for users who requested to join) */}
              {members.filter(m => m.status === 'pending').length > 0 && (
                <>
                  <h4 className="text-sm font-medium text-blue-400 mt-4 mb-2">Join Requests ({members.filter(m => m.status === 'pending').length})</h4>
                  {members.filter(m => m.status === 'pending').map(membership => (
                    <div key={membership.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg border border-blue-600/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-semibold overflow-hidden">
                          {membership.user?.profile_picture ? (
                            <img src={membership.user.profile_picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            (membership.user?.first_name?.[0] || 'U')
                          )}
                        </div>
                        <div>
                          <p className="font-medium">
                            {membership.user?.display_name || `${membership.user?.first_name} ${membership.user?.last_name}`}
                          </p>
                          <p className="text-xs text-blue-400">Requested to join</p>
                        </div>
                      </div>

                      {canManage && (
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={async () => {
                              try {
                                await axios.post(`${API_URL}/api/clubs/${club.id}/members/${membership.user_id}/approve?user_id=${user.id}`);
                                toast.success(`${membership.user?.display_name || 'User'} approved!`);
                                fetchClubData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to approve');
                              }
                            }}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-member-${membership.user_id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            onClick={async () => {
                              try {
                                await axios.post(`${API_URL}/api/clubs/${club.id}/members/${membership.user_id}/deny?user_id=${user.id}`);
                                toast.success('Request denied');
                                fetchClubData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to deny');
                              }
                            }}
                            size="sm"
                            variant="outline"
                            className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                            data-testid={`deny-member-${membership.user_id}`}
                          >
                            Deny
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Empty State */}
              {members.length === 0 && (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No members yet</p>
                </div>
              )}

              {/* Leave Club Button */}
              {clubData.user_role && clubData.user_role !== 'owner' && (
                <Button onClick={handleLeaveClub} variant="outline" className="w-full mt-4 border-red-600 text-red-500">
                  Leave Club
                </Button>
              )}
              
              {/* Invite Button for Members Tab */}
              {canManage && (
                <Button onClick={() => setShowInviteModal(true)} className="w-full mt-2 bg-purple-600 hover:bg-purple-700">
                  <UserPlus className="h-4 w-4 mr-2" /> Invite Members
                </Button>
              )}
            </div>
          )}
        </div>
        </>
        )}

        {/* Edit Club Modal */}
        <ClubModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          club={clubData}
          user={user}
          onSave={() => { fetchClubData(); onUpdate(); }}
        />

        {/* Invite Member Modal */}
        <InviteMemberModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          club={clubData}
          user={user}
          onSuccess={fetchClubData}
        />

        {/* Last Person Standing Game Modal */}
        {gameMovie && (
          <LastPersonStanding
            isOpen={!!gameMovie}
            onClose={() => setGameMovie(null)}
            clubId={club.id}
            item={gameMovie}
            user={user}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Clubs;
